import React, {Fragment, useState} from 'react';

import {WithRouterProps} from 'react-router';

import {AcceptanceSidebarItem} from 'app/acceptance/components/AcceptanceSidebarItem';
import {GenericAcceptSidebar} from 'app/acceptance/components/GenericAcceptSidebar';
import {CommentsSidebarItem} from 'app/comments/components/CommentsSidebarItem';
import {GenericCommentsSidebar} from 'app/comments/components/GenericCommentsSidebar';
import {DetailPageActivitySidebar} from 'app/components/general/DetailPageActivitySidebar';
import {PolicyDetailPageReviewSidebar} from 'app/components/general/DetailPageReviewSidebar';
import {DetailPageSignoffSidebar} from 'app/components/general/DetailPageSignoffSidebar';
import {DocumentTeamsSidebar} from 'app/components/general/DocumentTeamsSidebar';
import {useCurrentUserState} from 'app/current-user/api';
import {DetailPageSidebarShortcuts} from 'app/general/shortcuts/DetailPageSidebarShortcuts';
import {PageSidebarStructure} from 'app/general/structures/PageSidebarStructure';
import {SidebarItem} from 'app/general/structures/SidebarItem';
import VersionsSidebarItem from 'app/procedures/components/ProcedureDetailPage/ProcedurePageSidebar/VersionsSidebarItem';
import {currVersionTooltipTextSelector} from 'app/procedures/selectors';
import {addPolicies, removeItem} from 'app/teams/actions';
import {
  getObjectActivityURL,
  getObjectCommentsURL,
  getObjectCurrentVersionURL,
  getObjectReviewURL,
  getObjectSignoffURL,
  getObjectTeamsURL,
  getPolicyProceduresURL,
  getPolicyProcessesURL,
} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {getPathFromURL} from 'app/utils/urls';

import {PolicyProceduresSidebar} from './PolicyProceduresSidebar';
import {PolicyProcessesSidebar} from './PolicyProcessesSidebar';
import {PolicyVersionsSidebar} from './PolicyVersionsSidebar';

import type {PolicyRouteType} from 'app/policies/types';
import type {
  AcceptanceSerializedType,
  DetailPolicySerializedType,
  DetailPolicyVersionSerializedType,
  ListPolicyVersionSerializedType,
} from 'sweet-types';

const procedureIcon = getContentType('procedure').icon;
const policyIcon = getContentType('policy').icon;
const processIcon = getContentType('proceduregroup').icon;
const teamIcon = getContentType('team').icon;

type Props = {
  policy: DetailPolicySerializedType;
  versionId?: number | string;
  acceptance?: AcceptanceSerializedType;
  fetchPolicy: () => Promise<any>;
  fetchPolicyVersions: () => Promise<any>;
  version: DetailPolicyVersionSerializedType | undefined;
  versions: Array<ListPolicyVersionSerializedType>;
  router: WithRouterProps['router'];
  location: WithRouterProps['location'];
  route: PolicyRouteType;
};

export const PolicyPageSidebar = ({
  acceptance,
  location,
  router,
  route,
  policy,
  version,
  versions,
  versionId,
  fetchPolicy,
  fetchPolicyVersions,
}: Props) => {
  const user = useCurrentUserState().data!;
  const contentArgs = {
    target: gt('this policy'),
  };

  const [collapsed, setCollapsed] = useState(false);

  const closeInnerSidebar = () => {
    router.replace(policy.html_url);
  };
  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const getVersionsURL = () => {
    return getPathFromURL(
      getObjectCurrentVersionURL(policy, versionId, location.search)
    );
  };

  const getProceduresURL = () => {
    return getPolicyProceduresURL(policy);
  };
  const getProcessesURL = () => getPolicyProcessesURL(policy);
  const getCommentsURL = (): string => {
    return getObjectCommentsURL(policy, versionId, location.search);
  };
  const getActivityURL = (): string => {
    return getObjectActivityURL(policy, location.search);
  };
  const getReviewURL = (): string => {
    return getObjectReviewURL(policy, location.search);
  };
  const getTeamURL = (): string => {
    return getObjectTeamsURL(policy, location.search);
  };
  const goToEditURL = () => {
    router.push(`${policy.html_url}edit/`);
  };
  const getSignoffURL = (): string => {
    return getObjectSignoffURL(policy, location.search);
  };

  const {
    showVersions,
    showProcedures,
    showComments,
    showActivity,
    showAccept,
    showReview,
    showProcesses,
    showTeams,
    showSignoff,
  } = route.props;
  return (
    <DetailPageSidebarShortcuts
      openVersions={() => router.push(getVersionsURL())}
      openComments={() => router.push(getCommentsURL())}
      openActivities={() => router.push(getActivityURL())}
      edit={goToEditURL}
    >
      <PageSidebarStructure
        innerContent={
          showVersions === true ? (
            <PolicyVersionsSidebar
              canDelete={policy.can_delete}
              collapsed={collapsed}
              onToggleCollapsed={toggleCollapsed}
              selectedVersion={version}
              closeSidebar={closeInnerSidebar}
              objectId={policy.id}
            />
          ) : showProcedures === true ? (
            <PolicyProceduresSidebar
              policyId={policy.id}
              policyURL={policy.url}
              onClose={closeInnerSidebar}
            />
          ) : showProcesses === true ? (
            <PolicyProcessesSidebar
              policyId={policy.id}
              policyURL={policy.url}
              onClose={closeInnerSidebar}
            />
          ) : showComments === true && versionId != null ? (
            <GenericCommentsSidebar
              contentType="policy"
              mentionsContentType="policy"
              addContentType="policyversion"
              objectId={policy.id}
              versionId={versionId}
              canDeleteComment={user.is_manager}
              toggleSidebar={closeInnerSidebar}
              loadAdditionalData={fetchPolicyVersions}
            />
          ) : showTeams === true ? (
            <DocumentTeamsSidebar
              contentType="policy"
              add={addPolicies}
              remove={removeItem}
              onClose={closeInnerSidebar}
              canEdit={policy.can_edit}
              url={policy.url}
              id={policy.id}
              fetch={fetchPolicy}
              team_memberships={policy.team_memberships}
              teams={policy.team_names}
            />
          ) : showActivity === true ? (
            <DetailPageActivitySidebar
              title={gt('Policy Activity')}
              objectId={policy.id}
              showExportButton={user.is_manager}
              contentType="policy"
              contentArgs={contentArgs}
              toggleSidebar={closeInnerSidebar}
            />
          ) : showReview === true && policy.current_version != null ? (
            <PolicyDetailPageReviewSidebar
              title={gt('Policy Review')} // $FlowFixMe - no idea the reason Flow doesn't accept the correct object type here
              object={policy}
              contentType="policy"
              versionId={policy.current_version.id}
              versionContentType={'policyversion'}
              canReview={policy.can_edit}
              onReview={fetchPolicy}
              onToggleReview={fetchPolicy}
              toggleSidebar={closeInnerSidebar}
            />
          ) : showAccept === true && version != null ? (
            <GenericAcceptSidebar
              acceptance={acceptance}
              versionURL={version.url}
              versionApprovedAt={version.approved_at}
              versionApprovedBy={version.approved_by}
              userId={user.id}
              documentId={policy.id}
              documentType={policy.content_type}
              documentHtmlURL={policy.html_url}
              toggleSidebar={closeInnerSidebar}
              nextReviewDate={policy.next_review_at}
            />
          ) : showSignoff ? (
            <DetailPageSignoffSidebar
              onClose={closeInnerSidebar}
              title={gt('Sign Off Log')}
              document={policy}
              refreshFunc={fetchPolicy}
            />
          ) : null
        }
      >
        {(showTooltip, hideTooltip) => {
          const commonProps = {
            forceTooltipShow: showTooltip,
            onClick: hideTooltip,
          };
          return (
            <Fragment>
              <SidebarItem
                title={currVersionTooltipTextSelector(policy)}
                icon={policyIcon}
                onlyActiveOnIndex={true}
                link={getPathFromURL(policy.html_url)}
                {...commonProps}
              />
              <VersionsSidebarItem
                link={getVersionsURL()}
                collapsed={collapsed}
                active={showVersions}
                versions={versions}
                {...commonProps}
              />
              <AcceptanceSidebarItem
                document={policy}
                contentType="policy"
                location={location}
                {...commonProps}
              />
              <SidebarItem
                title={gt('Related Procedures')}
                icon={procedureIcon}
                link={getProceduresURL}
                {...commonProps}
              />
              <SidebarItem
                title={gt('Related Processes')}
                icon={processIcon}
                link={getProcessesURL}
                {...commonProps}
              />
              <CommentsSidebarItem
                contentType={'policy'}
                objectId={policy.id}
                active={showComments}
                link={getCommentsURL}
                {...commonProps}
              />
              <SidebarItem
                title={gt('Related Teams')}
                active={showTeams}
                icon={teamIcon}
                link={getTeamURL}
                {...commonProps}
              />
              <SidebarItem
                title={gt('Policy Activity')}
                active={showActivity}
                icon="fa fa-signal"
                link={getActivityURL}
                {...commonProps}
              />
              <SidebarItem
                disabled={policy.approved_at == null}
                disabledTooltipText={gt(
                  'Only an approved policy can be reviewed.'
                )}
                title={gt('Review')}
                active={showReview}
                icon="fa fa-certificate"
                link={getReviewURL}
                {...commonProps}
              />
              <SidebarItem
                icon="fa fa-signature"
                title={gt('Sign Off Log')}
                active={showSignoff}
                link={getSignoffURL}
                {...commonProps}
              />
            </Fragment>
          );
        }}
      </PageSidebarStructure>
    </DetailPageSidebarShortcuts>
  );
};
